import clsx from "clsx";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.mjs?url";
import { useEffect, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { LearnerFileType } from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { ZoomControls } from "@components/DocumentReviewModal/components/ZoomControls";
import { useZoomAndPan } from "@components/DocumentReviewModal/hooks/useZoomAndPan";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

interface DocumentViewerPDFProps {
  file: LearnerFileType;
  width: number;
}

export const DocumentViewerPDF: React.FC<DocumentViewerPDFProps> = ({ file, width }) => {
  const [numPages, setNumPages] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);

  const { scale, isDragging, scrollRef, zoomIn, zoomOut, onMouseDown, onMouseMove, onMouseUp } = useZoomAndPan(
    file.signedS3LearnerFile,
  );

  // Reset loading state when the URL changes
  useEffect(() => {
    setIsLoading(true);
    setNumPages(undefined);
  }, [file.signedS3LearnerFile]);

  return (
    <div className="relative flex flex-col h-full">
      {isLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-text-secondary z-10 bg-limestone-100">
          <Loader className="w-6 h-6 mr-0 ml-0 text-text-secondary" />
          <p className="text-sm">Loading preview…</p>
        </div>
      )}
      <div
        ref={scrollRef}
        className={clsx("overflow-auto flex-1 select-none", isDragging ? "cursor-grabbing" : "cursor-grab")}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <div className="flex flex-col items-center py-4 gap-4 min-w-max">
          <Document
            key={file.signedS3LearnerFile}
            file={file.signedS3LearnerFile}
            onLoadSuccess={({ numPages }) => {
              setNumPages(numPages);
              setIsLoading(false);
            }}
            onLoadError={() => setIsLoading(false)}
          >
            {numPages &&
              Array.from({ length: numPages }, (_, idx) => (
                <Page key={idx + 1} pageNumber={idx + 1} width={width * scale} />
              ))}
          </Document>
        </div>
      </div>

      <ZoomControls scale={scale} onZoomIn={zoomIn} onZoomOut={zoomOut} />
    </div>
  );
};
